import type { PropsWithChildren } from 'react';
import React from 'react';
import type { SubjectActions, SubjectKey, SubjectPayload, SubjectType } from '../../types/acl-types.ts';
import { useAcl } from './acl-provider.tsx';

type EnforceProps<TKey extends SubjectKey> = {
    action: SubjectActions<TKey>;
    subject: SubjectType<TKey>;
    field?: keyof SubjectPayload<TKey>;
    deny?: boolean;
} & PropsWithChildren;

export const Enforce = <TKey extends SubjectKey>({
    children,
    action,
    subject,
    field,
    deny,
}: EnforceProps<TKey>) => {
    const authorizer = useAcl();

    const isAllowed = authorizer.can(action, subject, field);

    /**
     * We don't need async state management here,
     * because the ACL check is synchronous.
     *
     * If needed in the future, we can add a loading state
     * https://github.com/streamich/react-use/blob/master/docs/useAsync.md
     */
    const state = {
        loading: false,
        value: deny ? !isAllowed : isAllowed,
    };

    const child = React.Children.toArray(children).find((child) => {
        if (React.isValidElement(child)) {
            if (state.loading && child.type === EnforceLoading) {
                return child;
            }

            if (state.value && child.type === Enforce.Allowed) {
                return child;
            }

            if (!state.value && child.type === Enforce.Denied) {
                return child;
            }
        }
    });

    return <>{child}</>;
};

const EnforceAllowed = ({ children }: PropsWithChildren) => {
    return children;
};

const EnforceDenied = ({ children }: PropsWithChildren) => {
    return children;
};

const EnforceLoading = ({ children }: PropsWithChildren) => {
    return children;
};

Enforce.Allowed = EnforceAllowed;
Enforce.Denied = EnforceDenied;
Enforce.Loading = EnforceLoading;

export default Enforce;
